import defaultConfig from './default';
import { AppConfig } from './types';

// Importujmy dostępne konfiguracje klientów
import eltimexConfig from './client-overrides/eltimex';
import decoConfig from './client-overrides/deco';

// Mapa konfiguracji klientów
const clientConfigs: { [key: string]: Partial<AppConfig> } = {
  eltimex: eltimexConfig,
  deco: decoConfig,
};

/**
 * Funkcja głęboko łącząca obiekty konfiguracyjne
 */
function mergeDeep<T extends { [key: string]: any }>(
  target: T,
  source: Partial<T>,
): T {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      const sourceValue = source[key];
      const targetValue = target[key];

      if (isObject(sourceValue)) {
        if (!(key in target)) {
          Object.assign(output, { [key]: sourceValue });
        } else if (isObject(targetValue)) {
          // Upewniamy się, że oba wartości są obiektami przed wywołaniem mergeDeep
          output[key as keyof T] = mergeDeep(targetValue, sourceValue!);
        } else {
          // Jeśli target[key] nie jest obiektem, ale źródło jest, używamy źródła
          Object.assign(output, { [key]: sourceValue });
        }
      } else if (sourceValue !== undefined) {
        Object.assign(output, { [key]: sourceValue });
      }
    });
  }

  return output;
}

function isObject(item: any): boolean {
  return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Ładuje konfigurację dla określonego klienta
 * @param {string} clientId - identyfikator klienta
 * @returns {AppConfig} finalConfig - połączona konfiguracja
 */
export function loadConfig(
  clientId: string = process.env.REACT_APP_CLIENT_ID || 'default',
): AppConfig {
  try {
    let clientConfig: Partial<AppConfig> = {};

    // Wykorzystanie predefiniowanych konfiguracji klientów
    if (clientId !== 'default' && clientId in clientConfigs) {
      clientConfig = clientConfigs[clientId];
    } else if (clientId !== 'default') {
      console.warn(
        `Nie znaleziono konfiguracji dla klienta ${clientId}, używam domyślnej.`,
      );
    }

    // Łączenie konfiguracji domyślnej z konfiguracją klienta
    const finalConfig = mergeDeep(defaultConfig, clientConfig);

    // Nadpisanie wartości z zmiennych środowiskowych (opcjonalnie)
    if (process.env.REACT_APP_COMPANY_NAME) {
      finalConfig.company.name = process.env.REACT_APP_COMPANY_NAME;
    }
    if (process.env.REACT_APP_PRIMARY_COLOR) {
      finalConfig.theme.colors.primary = process.env.REACT_APP_PRIMARY_COLOR;
    }

    return finalConfig;
  } catch (error) {
    console.error('Błąd podczas ładowania konfiguracji:', error);
    return defaultConfig;
  }
}

// Eksportujemy domyślną konfigurację na podstawie CLIENT_ID
export const config: AppConfig = loadConfig();

// Dodatkowo możemy zachować domyślny eksport dla wstecznej kompatybilności
export default config;
